import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, PiggyBank } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { TYPES } from "./meta";
import ApplyDialog from "./ApplyDialog";

export default function EstimateNotes({ projectId, onApplied }) {
  const { data, reload } = useFetch(`/projects/${projectId}/prestart/estimate-notes`);
  const [applying, setApplying] = useState(null);
  if (!data?.items?.length) return null;
  const done = () => { reload(); onApplied?.(); };
  const toggle = async (it) => { if (!it.applied) { setApplying(it); return; } try { await api.post(`/prestart/${it.id}/applied`); done(); } catch (e) { toast.error(errMsg(e)); } };
  const pending = data.items.filter((i) => !i.applied);
  return (
    <section className="rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="estimate-prestart-notes">
      <div className="flex items-center justify-between gap-2">
        <p className="eyebrow flex items-center gap-1.5"><PiggyBank className="h-3.5 w-3.5" />Pre-start notes for estimating</p>
        <Link to={`/projects/${projectId}?tab=prestart`} className="text-xs text-muted-foreground hover:text-foreground" data-testid="estimate-prestart-link">Open planning</Link>
      </div>
      {data.potential_saving > 0 && <p className="mt-1 text-xs text-muted-foreground">~<span className="stat" data-testid="estimate-potential-saving">{money(data.potential_saving)}</span> in potential savings not yet applied. Nothing changes in the estimate until you apply it yourself.</p>}
      <ul className="mt-3 space-y-2">
        {data.items.map((i) => {
          const T = TYPES[i.type] || TYPES.question;
          return (
            <li key={i.id} className={`flex items-start gap-2 text-sm ${i.applied ? "text-muted-foreground" : ""}`} data-testid={`estimate-note-${i.id}`}>
              <T.icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className={i.applied ? "line-through" : "font-medium"}>{i.title}</span>
                <span className="block text-xs text-muted-foreground">{T.label}{i.supplier ? ` · ${i.supplier}` : ""}{i.saving_amount ? ` · ~${money(i.saving_amount)}` : ""}{i.applied ? ` · applied by ${i.applied.by}` : ""}</span>
              </span>
              <Button size="sm" variant={i.applied ? "ghost" : "outline"} className="h-7 shrink-0 rounded-full px-2 text-xs" onClick={() => toggle(i)} data-testid={`estimate-note-apply-${i.id}`}>{i.applied ? <CheckCircle2 className="h-3.5 w-3.5" /> : "Applied"}</Button>
            </li>
          );
        })}
      </ul>
      {pending.length === 0 && <p className="mt-2 text-xs text-muted-foreground">All pre-start notes applied.</p>}
      <ApplyDialog item={applying} onClose={() => setApplying(null)} onDone={done} />
    </section>
  );
}
